import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ZanYearPoint {
  year: number;
  ha: number;
}

/** Decomposition of artificialisation by type (from compute_zan_status detail). */
export interface ZanInnerDetail {
  batiments_ha: number;
  voirie_ha: number;
}

/** ZAN law target metrics (from compute_zan_status zan block). */
export interface ZanStatus {
  objectif_annee: number;
  reduction_cible_pct: number;
  rythme_estime_ha_an: number;
  rythme_cible_ha_an: number;
  quota_restant_ha: number;
  alerte: boolean;
}

/** Full detail block from compute_zan_status (nested inside the API response). */
export interface ZanDetailFull {
  territory_code: string;
  surface_totale_ha: number;
  surface_artificialisee_ha: number;
  ratio_artificialisation_pct: number;
  detail: ZanInnerDetail;
  zan: ZanStatus;
}

/** Shape of GET /territories/{insee}/zan — matches territories.py contract. */
export interface ZanData {
  /** Territory total surface (ha) — used as ZAN envelope ceiling. */
  envelope_ha: number;
  /** Total artificialised surface (buildings + roads, in ha). */
  consumed_ha: number;
  /** 11 data points from (current_year − 10) to current_year, inclusive. */
  by_year: ZanYearPoint[];
  /** Required annual pace (ha/year) to meet the ZAN target. */
  trajectory_required: number;
  /** Full status payload from compute_zan_status. */
  detail: ZanDetailFull;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches ZAN (Zéro Artificialisation Nette) monitoring data for a commune.
 * GET /territories/{insee}/zan
 */
export function useZan(insee: string | null) {
  return useQuery<ZanData>({
    queryKey: ['zan', insee],
    queryFn: async () => {
      const { data } = await api.get<ZanData>(`/territories/${insee}/zan`);
      return data;
    },
    enabled: !!insee,
  });
}
